import { useEffect, useRef } from 'react'
import type { Me } from '@region13/shared-types'
import { useCan } from '@region13/ui'
import { capture, identifyUser } from './client'
import type { Surface } from './surfaces'

type Caller = Pick<Me, 'email' | 'roles'>

/** Identify the signed-in caller, and record one `surface_opened`, as soon as
 *  the surface's EXISTING `me` query resolves. Takes `me` as an argument rather
 *  than fetching: every surface has already loaded it, and a second request for
 *  analytics would be a real cost on a coach's phone.
 *
 *  ⚠️ THE TWO CALLS HAVE DIFFERENT LIFETIMES and must not share one guard.
 *  Identify runs on every change of `me`; `surface_opened` runs once. */
export function useAnalyticsIdentity(surface: Surface, me: Caller | null | undefined): void {
  const opened = useRef(false)
  useEffect(() => {
    if (!me) return
    // ⚠️ EVERY CHANGE OF `me`, NOT ONCE. On the four OTP surfaces a signed-out
    // visitor's `GET /api/auth/me` resolves non-null — `{ email: null }` on
    // coach, referee and fields, a folded 401 on stats — so the first `me` a
    // page sees is normally the signed-out one. Entering the 6-digit code
    // invalidates `['me']` in place, with no page load, and this hook is
    // mounted above the router so it never unmounts. A once-only guard here
    // latched on that signed-out object, and nobody who signed in during the
    // page session was ever identified: with `person_profiles:
    // 'identified_only'` that is not a missing property, it is no person
    // profile at all. `identifyUser` dedupes by email in `client.ts`, so
    // re-running costs nothing and a repeat is a no-op.
    identifyUser({ surface, me })
    // ⚠️ ONCE PER SURFACE PER PAGE SESSION, and guarded with a ref rather than
    // an effect dependency list. `capture` has no dedup of its own, React 19's
    // StrictMode double-invokes effects in development, and `surface_opened`
    // is the adoption number this whole install exists to produce — firing it
    // twice doubles it. The guard is set before the call rather than after:
    // `capture` is a guarded no-op in `client.ts` and never throws, so
    // tripping it early cannot lose an open, while setting it late would risk
    // the opposite — a throw leaving it unset and every future render
    // retrying the fire.
    if (opened.current) return
    opened.current = true
    // Roles is a comma-joined label, not an array: `capture` props are scalars,
    // and the array form is already on the person profile from `identifyUser`.
    capture('surface_opened', { roles: me.roles.join(',') })
  }, [surface, me])
}

/** The same thing for the five SPAs that host a `CapabilityProvider`: reads the
 *  shared `['me']` query through `useCan`, so it costs no extra request.
 *  Renders nothing — drop it anywhere inside the provider. */
export function AnalyticsIdentity({ surface }: { surface: Surface }): null {
  const { me } = useCan()
  useAnalyticsIdentity(surface, me.data)
  return null
}
